import React, { useState, useRef } from 'react';
import { useT } from '../i18n';
import { loadImageForEditing } from '../utils/imageCompress';
import AvatarCropper from './AvatarCropper';
import './DesignerProfileFields.css';

// ── AVATAR PICKER ────────────────────────────────────────────────────────────
// Profile picture chooser, shared by BOTH account types — designers (inside the
// profile block) and customers (registration + Settings). Extracted from
// DesignerProfileFields so the two can't drift: same compression, same limits,
// same error messages, one place to change any of them.
//
// The file never leaves the browser uncompressed; `compressAvatar` crops to a
// square and encodes to a small JPEG data URL first (see utils/imageCompress).
export default function AvatarPicker({ value, onChange, disabled = false, label }) {
  const { t } = useT();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The decoded image awaiting a crop. Nothing is encoded or handed to the
  // parent until the crop is accepted, so cancelling leaves the old picture.
  const [pending, setPending] = useState(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';                      // allow re-picking the same file
    if (!file) return;
    setError('');
    if (!/^image\//.test(file.type)) { setError(t('ds_err_avatar')); return; }
    setBusy(true);
    try {
      // Decode only — the crop editor needs the real pixel dimensions to work
      // out its scales, and compressing first would throw away the resolution
      // that zooming in depends on.
      setPending(await loadImageForEditing(file));
    } catch {
      setError(t('ds_err_avatar'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dpf-avatar-row">
      <button
        type="button"
        className="dpf-avatar"
        onClick={() => fileRef.current?.click()}
        disabled={disabled || busy}
        aria-label={label || t('ds_avatar')}
      >
        {value
          ? <img src={value} alt="" />
          : <span className="dpf-avatar-ph">{busy ? '…' : '+'}</span>}
      </button>

      <div className="dpf-avatar-meta">
        <span className="dpf-avatar-title">{label || t('ds_avatar')}</span>
        <span className="dpf-hint">{t('ds_avatar_hint')}</span>
        {value && !disabled && (
          <button type="button" className="dpf-link-btn" onClick={() => onChange('')}>
            {t('ds_avatar_remove')}
          </button>
        )}
        {error && <span className="dpf-err">{error}</span>}
      </div>

      <input
        ref={fileRef} type="file" accept="image/*" hidden
        onChange={handleFile} disabled={disabled}
      />

      {pending && (
        <AvatarCropper
          image={pending}
          onCancel={() => { pending.revoke(); setPending(null); }}
          onDone={(dataUrl) => { pending.revoke(); setPending(null); onChange(dataUrl); }}
        />
      )}
    </div>
  );
}
